import { useMemo } from 'react';
import { StanButton } from '@/components';
import { useDefaultStore } from '@/stores/defaultStore';
import { useStanRouter } from '@/hooks/useStanRouter';
import type { Page, PageType } from '@/types/store';
import {
    buildFunnelPageTree,
    buildFunnelPageTreePositions,
    getFunnelPageRelationships,
    MIN_FUNNEL_ROWS,
    toFunnelPageItem,
} from '../../../logic';
import type { FunnelPageProductInfo } from '../../../types';
import { getSuggestedPageAt } from './FunnelPagesExpandedLayout';
import { FunnelPageNavGroup } from '../../editor/navigation/FunnelPageNavGroup';
import { SuggestedFunnelPageNavRow } from '../../editor/navigation/SuggestedFunnelPageNavRow';

const FUNNEL_PAGE_TYPE: PageType = 'funnel-page' as PageType;

interface FunnelPagesExpandedProps {
    storeId: number;
    pages: Page[];
}

export const FunnelPagesExpanded = ({ storeId, pages }: FunnelPagesExpandedProps) => {
    const { push } = useStanRouter();
    const { store: linksiteStore } = useDefaultStore();

    const funnelPages = useMemo(() => {
        const sorted = pages.map(toFunnelPageItem).sort((a, b) => a.order - b.order || a.page_id - b.page_id);
        return sorted.map((p, idx) => ({ ...p, order: idx + 1 }));
    }, [pages]);

    const productInfoByPageId = useMemo<Record<number, FunnelPageProductInfo | undefined>>(() => {
        const linksitePagesById = new Map((linksiteStore?.pages ?? []).map(p => [p.page_id, p]));

        return Object.fromEntries(
            funnelPages.map(page => {
                if (page.referencePageId) {
                    const referenced = linksitePagesById.get(page.referencePageId);
                    if (referenced) {
                        const info: FunnelPageProductInfo = {
                            title: referenced.data?.product?.title?.trim() || 'Untitled Product',
                            type: referenced.type,
                            price: referenced.data?.product?.price?.amount,
                            currency: referenced.data?.product?.price?.currency,
                            image: referenced.data?.product?.image,
                        };
                        return [page.page_id, info];
                    }
                }

                if (page.type !== FUNNEL_PAGE_TYPE) {
                    const info: FunnelPageProductInfo = {
                        title: page.title,
                        type: page.type,
                        price: page.price,
                        currency: page.currency,
                        image: page.image,
                    };
                    return [page.page_id, info];
                }

                return [page.page_id, undefined];
            })
        );
    }, [funnelPages, linksiteStore?.pages]);

    const rawSlots = useMemo(() => buildFunnelPageTree(funnelPages), [funnelPages]);
    const relationshipByPageId = useMemo(() => getFunnelPageRelationships(funnelPages), [funnelPages]);
    const positionByPageId = useMemo(() => buildFunnelPageTreePositions(rawSlots), [rawSlots]);

    const handleOpenFunnel = () => push(`/funnels/${storeId}`);
    const isEmpty = funnelPages.length === 0;

    const trailingSuggestedCount = Math.max(MIN_FUNNEL_ROWS - positionByPageId.size, 0);

    return (
        <div className='flex flex-col gap-2 px-4 pt-2 pb-2 md:px-7'>
            <div className='flex flex-col gap-3'>
                {rawSlots.map(slot => {
                    if (slot.kind !== 'page') return null;
                    return (
                        <FunnelPageNavGroup
                            key={slot.page.page_id}
                            slot={slot}
                            parentStepNumber={null}
                            selectedPageId={null}
                            productInfoByPageId={productInfoByPageId}
                            relationshipByPageId={relationshipByPageId}
                            positionByPageId={positionByPageId}
                            onSelectPage={handleOpenFunnel}
                        />
                    );
                })}

                {Array.from({ length: trailingSuggestedCount }, (_, i) => {
                    const stepNumber = positionByPageId.size + i + 1;
                    return (
                        <SuggestedFunnelPageNavRow
                            key={`suggested-${stepNumber}`}
                            stepNumber={stepNumber}
                            title={getSuggestedPageAt(stepNumber - 1).title}
                            active={false}
                            onSelect={handleOpenFunnel}
                        />
                    );
                })}
            </div>

            {!isEmpty && (
                <div className='mt-2 flex flex-col gap-3'>
                    <StanButton
                        name={`open-funnel-${storeId}`}
                        variant='outline'
                        size='sm'
                        className='w-full md:w-fit'
                        onClick={handleOpenFunnel}
                    >
                        Open Funnel
                    </StanButton>
                </div>
            )}
        </div>
    );
};
